import type * as React from "react";

/**
 * The press handling a menu trigger needs to open on **release**, as the donor
 * does, rather than on press, as Radix does.
 *
 * Radix's `DropdownMenuTrigger` toggles from `pointerdown`
 * (`react-dropdown-menu/dist/index.mjs:74-79`). Every donor menu in this app
 * toggles from the button's `onClick` instead — `menu-button.tsx:93` calling
 * `setOpen(prev => !prev)` at `:75`, and `ActionMenu.tsx:60` — and neither they
 * nor anything in MUI's modal chain has a `mousedown` or `pointerdown` path. So
 * the donor's menu appears when the button is let go, and **a press that drags
 * off and releases elsewhere opens nothing at all**. Measured on both apps: the
 * Atlas gear opened on press and stayed open after a 400px drag-away release,
 * where modsy's opened on neither.
 *
 * `preventDefault` is what declines Radix: it composes its own handler *behind*
 * the child's with `composeEventHandlers`, which stands down on a
 * defaultPrevented event. Guarded to the primary button because that is the
 * only one Radix acts on, and cancelling the others would take the context menu
 * with it.
 *
 * Its own module, though `IconMenuButtonTrigger` is currently its only caller,
 * because the ordering here is subtle enough to be worth stating once where it
 * can be read on its own: the `preventDefault` depends on Radix composing
 * behind the child, and the `onClick` depends on the consumer running first so
 * it can still cancel the open. It briefly had a second caller — the catalog
 * batch toolbar's gear — and the two copies drifted apart within one review
 * round (#542, rounds 12 and the code review over them), which is why that
 * trigger is now the shared one rather than a sibling.
 *
 * The menu it drives must be **controlled**, since the open state has to be the
 * caller's for `toggle` to mean anything.
 */
export function menuTriggerPressProps<E extends HTMLElement>({
  toggle,
  onPointerDown,
  onClick,
}: {
  /** Flips the caller's own open state — what `onClick` calls. */
  toggle: () => void;
  onPointerDown?: React.PointerEventHandler<E>;
  onClick?: React.MouseEventHandler<E>;
}): {
  onPointerDown: React.PointerEventHandler<E>;
  onClick: React.MouseEventHandler<E>;
} {
  return {
    onPointerDown: (event) => {
      onPointerDown?.(event);
      if (event.button === 0 && !event.ctrlKey) {
        event.preventDefault();
        // Cancelling `pointerdown` suppresses the compatibility `mousedown`,
        // and with it the focus the browser gives a pressed button. MUI's
        // `ButtonBase` cancels nothing, so the donor's button is focused from
        // the press whatever the release goes on to do.
        event.currentTarget.focus();
      }
    },
    // Consumer first, then ours, skipped if the consumer cancelled — Radix's
    // own `composeEventHandlers` order (`react-primitive`), and the reason a
    // caller can call `preventDefault()` to stop the menu opening the way it
    // can on any other Radix trigger. Ours ran first until #479's cross-review,
    // run 2, which made the cancel impossible to express.
    onClick: (event) => {
      onClick?.(event);
      if (!event.defaultPrevented) toggle();
    },
  };
}
